import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Standardized bulk export tools
import { MRT_ColumnDef, MRT_SortingState } from "material-react-table";
import SearchRounded from "@mui/icons-material/SearchRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import TableChartRounded from "@mui/icons-material/TableChartRounded";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { format } from "date-fns";

import { DefaultPage } from "../../layouts/default-page";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { useTableState } from "../../hooks/useTableState";
import { StatusChip } from "../../components/common/StatusChip";
import { RowActions } from "../../components/common/RowActions";
import { Notify } from "../../layouts/notify";
import { ExportLoadingBackdrop } from "../../components/common/ExportLoadingBackdrop";
import {
    fetchAllInBatches,
    mapRowsInChunks,
    yieldToMainThread,
} from "../../utils/exportUtils";

import {
    useGetClaimedTicketRequestsFromDeputiesQuery,
    useLazyGetClaimedTicketRequestsFromDeputiesQuery,
    useDeleteClaimedTicketRequestMutation,
} from "../../services/report.query";

type ClaimedTicketRequestRow = {
    number: number;
    id: number;
    code: string;
    request_datetime: string;
    deputy: string;
    companion: string;
    movie_title: string;
    mall: string;
    cinema: string;
    movie_datetime: string;
    is_offline: string;
    has_report_record: boolean;
    can_delete: boolean;
    total_count?: number;
};

const rowsPerPage = 100;

function DetailItem({
    label,
    value,
}: {
    label: string;
    value?: React.ReactNode;
}) {
    return (
        <Box>
            <Typography
                fontSize={12}
                fontWeight={700}
                color="text.secondary"
                textTransform="uppercase"
                mb={0.5}
            >
                {label}
            </Typography>

            <Typography fontSize={14.5} fontWeight={600} color="#0F172A">
                {value || "—"}
            </Typography>
        </Box>
    );
}

type TicketRequestDetailsModalProps = {
    open: boolean;
    row: ClaimedTicketRequestRow | null;
    onClose: () => void;
    onDelete: () => void;
    isDeleting: boolean;
};

function TicketRequestDetailsModal({
    open,
    row,
    onClose,
    onDelete,
    isDeleting,
}: TicketRequestDetailsModalProps) {
    const [confirmDelete, setConfirmDelete] = useState(false);

    const isOffline = row?.is_offline === "Yes";
    const canDelete = Boolean(row?.can_delete);

    const handleClose = () => {
        if (isDeleting) return;
        setConfirmDelete(false);
        onClose();
    };

    const handleDeleteClick = () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }

        onDelete();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: "6px",
                    border: "1px solid #E2E8F0",
                },
            }}
        >
            <DialogTitle sx={{ pb: 1.5 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                >
                    <Box>
                        <Typography fontSize={20} fontWeight={800} color="#0F172A">
                            Claimed Ticket Request Details
                        </Typography>
                        <Typography fontSize={13} color="text.secondary">
                            Review detailed information and report linkage for this ticket request.
                        </Typography>
                    </Box>
                    {row && (
                        <StatusChip
                            label={isOffline ? "OFFLINE CLAIM" : "ONLINE CLAIM"}
                            tone={isOffline ? "warning" : "info"}
                        />
                    )}
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ py: 3 }}>
                {row && (
                    <Box>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <DetailItem label="Ticket Code" value={row.code} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem
                                    label="Movie Date Time"
                                    value={row.movie_datetime}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem
                                    label="Request Date Time"
                                    value={row.request_datetime}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem label="Deputy" value={row.deputy} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem label="Companion" value={row.companion} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem label="Movie Title" value={row.movie_title} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem label="Mall" value={row.mall} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem label="Cinema" value={row.cinema} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem
                                    label="Submission Channel"
                                    value={isOffline ? "Offline Sync" : "Online Direct"}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DetailItem
                                    label="Watched Movie Report"
                                    value={
                                        row.has_report_record
                                            ? "Report Submitted"
                                            : "No Report Record"
                                    }
                                />
                            </Grid>
                        </Grid>

                        {!canDelete && (
                            <Alert
                                severity="warning"
                                sx={{
                                    mt: 3,
                                    borderRadius: "4px",
                                    fontSize: 13,
                                }}
                            >
                                This ticket request cannot be deleted because it already has a
                                related watched movie report record.
                            </Alert>
                        )}

                        {confirmDelete && canDelete && (
                            <Alert
                                severity="error"
                                sx={{
                                    mt: 3,
                                    borderRadius: "4px",
                                    fontSize: 13,
                                }}
                            >
                                Click Delete again to confirm. This action will permanently remove
                                the ticket request.
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} color="inherit" disabled={isDeleting}>
                    Close
                </Button>

                <Button
                    color="error"
                    variant={confirmDelete ? "contained" : "outlined"}
                    onClick={handleDeleteClick}
                    disabled={!canDelete || isDeleting}
                    sx={{
                        borderRadius: "4px",
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    {isDeleting
                        ? "Deleting..."
                        : confirmDelete
                            ? "Confirm Delete"
                            : "Delete"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ClaimedTicketRequestsPage() {
    const filterWidth = { minWidth: 170 };

    const defaultFilters = useMemo(
        () => ({
            requestStart: dayjs().startOf("month").format("YYYY-MM-DD"),
            requestEnd: dayjs().endOf("month").format("YYYY-MM-DD"),
            movieStart: "",
            movieEnd: "",
            deputy: "",
            companion: "",
            movieTitle: "",
            mall: "",
            offlineOnly: false,
        }),
        []
    );

    // Persistent URL Table State: saves and restores page, filters, and sort order across route transitions
    const {
        page,
        pageSize,
        sortBy,
        sortOrder,
        filters,
        hasActiveFilters,
        setPage,
        setSorting: updateTableSorting,
        setFilters,
        resetFilters,
    } = useTableState(defaultFilters, {
        defaultPage: 1,
        defaultPageSize: rowsPerPage,
        defaultSortBy: "movie_datetime",
        defaultSortOrder: "desc",
    });

    // Local draft state for date pickers and inputs
    const [draftRequestStart, setDraftRequestStart] = useState<Dayjs | null>(
        filters.requestStart ? dayjs(filters.requestStart) : null
    );
    const [draftRequestEnd, setDraftRequestEnd] = useState<Dayjs | null>(
        filters.requestEnd ? dayjs(filters.requestEnd) : null
    );
    const [draftMovieStart, setDraftMovieStart] = useState<Dayjs | null>(
        filters.movieStart ? dayjs(filters.movieStart) : null
    );
    const [draftMovieEnd, setDraftMovieEnd] = useState<Dayjs | null>(
        filters.movieEnd ? dayjs(filters.movieEnd) : null
    );
    const [draftDeputy, setDraftDeputy] = useState(filters.deputy);
    const [draftCompanion, setDraftCompanion] = useState(filters.companion);
    const [draftMovieTitle, setDraftMovieTitle] = useState(filters.movieTitle);
    const [draftMall, setDraftMall] = useState(filters.mall);
    const [draftOfflineOnly, setDraftOfflineOnly] = useState(filters.offlineOnly);

    // Sync draft states when URL filters change
    useEffect(() => {
        setDraftRequestStart(filters.requestStart ? dayjs(filters.requestStart) : null);
        setDraftRequestEnd(filters.requestEnd ? dayjs(filters.requestEnd) : null);
        setDraftMovieStart(filters.movieStart ? dayjs(filters.movieStart) : null);
        setDraftMovieEnd(filters.movieEnd ? dayjs(filters.movieEnd) : null);
        setDraftDeputy(filters.deputy);
        setDraftCompanion(filters.companion);
        setDraftMovieTitle(filters.movieTitle);
        setDraftMall(filters.mall);
        setDraftOfflineOnly(filters.offlineOnly);
    }, [filters]);

    const [selectedRow, setSelectedRow] = useState<ClaimedTicketRequestRow | null>(null);

    const [deleteClaimedTicketRequest, { isLoading: isDeleting }] =
        useDeleteClaimedTicketRequestMutation();

    const requestParams = useMemo(() => {
        return {
            p_request_start_date: filters.requestStart || null,
            p_request_end_date: filters.requestEnd || null,
            p_movie_start_date: filters.movieStart || null,
            p_movie_end_date: filters.movieEnd || null,
            p_deputy: filters.deputy.trim() || null,
            p_companion: filters.companion.trim() || null,
            p_movie_title: filters.movieTitle.trim() || null,
            p_mall: filters.mall.trim() || null,
            p_is_offline: filters.offlineOnly ? true : null,
            p_sort_column: sortBy || "movie_datetime",
            p_sort_direction: sortOrder || "desc",
        };
    }, [filters, sortBy, sortOrder]);

    const listParams = useMemo(() => {
        const safePage = Math.max(page, 1);
        const safeOffset = (safePage - 1) * pageSize;

        return {
            ...requestParams,
            p_limit: pageSize,
            p_offset: safeOffset,
        };
    }, [requestParams, page, pageSize]);

    const {
        data: cachedData = [],
        currentData,
        isLoading,
        isFetching,
        isError,
        refetch: refetchList,
    } = useGetClaimedTicketRequestsFromDeputiesQuery(listParams, {
        refetchOnMountOrArgChange: true,
    });

    const data = currentData ?? cachedData ?? [];

    const [stableTotalCount, setStableTotalCount] = useState(0);

    useEffect(() => {
        if (currentData) {
            setStableTotalCount(Number(currentData?.[0]?.total_count ?? 0));
        }
    }, [currentData]);

    const totalCount = stableTotalCount;

    const totalPages = Math.max(
        1,
        Math.ceil(Number(totalCount || 0) / pageSize)
    );

    const handleSearch = () => {
        setSelectedRow(null);
        setFilters({
            requestStart: draftRequestStart ? draftRequestStart.format("YYYY-MM-DD") : "",
            requestEnd: draftRequestEnd ? draftRequestEnd.format("YYYY-MM-DD") : "",
            movieStart: draftMovieStart ? draftMovieStart.format("YYYY-MM-DD") : "",
            movieEnd: draftMovieEnd ? draftMovieEnd.format("YYYY-MM-DD") : "",
            deputy: draftDeputy.trim(),
            companion: draftCompanion.trim(),
            movieTitle: draftMovieTitle.trim(),
            mall: draftMall.trim(),
            offlineOnly: draftOfflineOnly,
        });
    };

    const handleClear = () => {
        setSelectedRow(null);
        resetFilters();
    };

    const handleDeleteSelected = async () => {
        if (!selectedRow) return;

        try {
            const result = await deleteClaimedTicketRequest({
                p_ticket_request_id: selectedRow.id,
            }).unwrap();

            if (!result.success) {
                alert(result.message || "Failed to delete ticket request.");
                return;
            }

            setSelectedRow(null);
            await refetchList();
        } catch (error: any) {
            alert(error?.message || "Failed to delete ticket request.");
        }
    };

    const [triggerFetchAllTickets] = useLazyGetClaimedTicketRequestsFromDeputiesQuery();
    const [isExporting, setIsExporting] = useState(false);
    const [exportMessage, setExportMessage] = useState("");
    const [exportProgress, setExportProgress] = useState<number | null>(null);

    const fetchAllClaimedTickets = async () => {
        return fetchAllInBatches<any>({
            batchSize: 250,
            knownTotal: Number(totalCount || 0),
            fetchBatch: async (offset, limit) => {
                const chunk = await triggerFetchAllTickets(
                    {
                        ...requestParams,
                        p_limit: limit,
                        p_offset: offset,
                    },
                    false
                ).unwrap();
                const rows = chunk || [];
                const batchTotal =
                    rows.length > 0 && rows[0].total_count
                        ? Number(rows[0].total_count)
                        : Number(totalCount || 0);
                return { rows, total: batchTotal };
            },
            onProgress: (fetched, total, percent) => {
                setExportProgress(percent);
                if (total && total > 0) {
                    setExportMessage(
                        `Fetching ticket requests: ${fetched.toLocaleString()} of ${total.toLocaleString()} records...`
                    );
                } else {
                    setExportMessage(
                        `Fetching ticket requests: ${fetched.toLocaleString()} records loaded...`
                    );
                }
            },
        });
    };

    const handleExportPDF = async () => {
        setIsExporting(true);
        setExportProgress(0);
        setExportMessage("Preparing to fetch all matching claimed ticket requests...");
        try {
            const allRows = await fetchAllClaimedTickets();
            if (allRows.length === 0) {
                Notify("info", "No records found to export.");
                return;
            }

            setExportProgress(88);
            setExportMessage(
                `Formatting ${allRows.length.toLocaleString()} ticket requests for PDF...`
            );
            await yieldToMainThread(30);

            const tableRows = await mapRowsInChunks(
                allRows,
                (row: any, idx: number) => [
                    idx + 1,
                    String(row.code || "—"),
                    String(row.movie_datetime || "—"),
                    String(row.deputy || "—"),
                    String(row.companion || "None"),
                    String(row.mall || "—"),
                    String(row.movie_title || "—"),
                    row.is_offline === "Yes" ? "Offline" : "Online",
                ],
                {
                    chunkSize: 1000,
                    startPercent: 88,
                    endPercent: 95,
                    onProgress: (processed, total, percent) => {
                        setExportProgress(percent);
                        setExportMessage(
                            `Preparing PDF rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
                        );
                    },
                }
            );

            setExportProgress(96);
            setExportMessage(
                `Rendering official PDF document (${allRows.length.toLocaleString()} ticket requests)...`
            );
            await yieldToMainThread(40);

            const doc = new jsPDF("landscape", "mm", "a4");

            doc.setFontSize(14);
            doc.setTextColor(0, 114, 188);
            doc.text("MOVIE AND TELEVISION REVIEW AND CLASSIFICATION BOARD", 14, 15);
            doc.setFontSize(10);
            doc.setTextColor(71, 85, 105);
            doc.text("Claimed Deputy Monitoring Ticket Requests Directory", 14, 21);

            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            const reqDateText = filters.requestStart || filters.requestEnd
                ? `Request Date: ${filters.requestStart || "Start"} to ${filters.requestEnd || "End"}`
                : "Request Date: All";
            const movieDateText = filters.movieStart || filters.movieEnd
                ? `Movie Date: ${filters.movieStart || "Start"} to ${filters.movieEnd || "End"}`
                : "Movie Date: All";
            doc.text(
                `Generated: ${format(new Date(), "MMM dd, yyyy · hh:mm a")}  |  ${reqDateText}  |  ${movieDateText}  |  Total Records: ${allRows.length.toLocaleString()}`,
                14,
                27
            );

            const tableColumn = [
                "#",
                "Ticket Code",
                "Movie Date & Time",
                "Deputy Name",
                "Companion",
                "Cinema / Mall",
                "Movie Title",
                "Submission",
            ];

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 32,
                theme: "grid",
                headStyles: {
                    fillColor: [0, 114, 188],
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                    fontSize: 8,
                },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                styles: {
                    fontSize: 7.5,
                    cellPadding: 2,
                    textColor: [15, 23, 42],
                    overflow: "linebreak",
                },
                margin: { top: 12, left: 14, right: 14, bottom: 14 },
            });

            const totalPages = (doc as any).internal.getNumberOfPages();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(7.5);
                doc.setTextColor(148, 163, 184);
                doc.text(
                    `Page ${i} of ${totalPages}`,
                    pageWidth - 14,
                    pageHeight - 6,
                    { align: "right" }
                );
            }

            setExportProgress(100);
            setExportMessage("Download starting...");
            await yieldToMainThread(20);

            doc.save(`MTRCB_Claimed_Ticket_Requests_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
            Notify("success", `Exported all ${allRows.length.toLocaleString()} ticket requests to PDF.`);
        } catch (err: any) {
            console.error("PDF export error:", err);
            Notify("error", err?.message || "Failed to generate PDF export.");
        } finally {
            setIsExporting(false);
            setExportProgress(null);
        }
    };

    const handleExportExcel = async () => {
        setIsExporting(true);
        setExportProgress(0);
        setExportMessage("Preparing to fetch all matching claimed ticket requests...");
        try {
            const allRows = await fetchAllClaimedTickets();
            if (allRows.length === 0) {
                Notify("info", "No records found to export.");
                return;
            }

            setExportProgress(88);
            setExportMessage(
                `Formatting ${allRows.length.toLocaleString()} ticket requests for Excel...`
            );
            await yieldToMainThread(30);

            const rows = await mapRowsInChunks(
                allRows,
                (item: any, idx: number) => ({
                    "No.": idx + 1,
                    "Ticket Code": item.code ?? "—",
                    "Movie Date & Time": item.movie_datetime ?? "—",
                    "Request Date & Time": item.request_datetime ?? "—",
                    "Deputy Name": item.deputy ?? "—",
                    "Companion": item.companion ?? "No Companion",
                    "Cinema / Mall": item.mall ?? "—",
                    "Movie Title": item.movie_title ?? "—",
                    "Submission": item.is_offline === "Yes" ? "Offline" : "Online",
                }),
                {
                    chunkSize: 1000,
                    startPercent: 88,
                    endPercent: 95,
                    onProgress: (processed, total, percent) => {
                        setExportProgress(percent);
                        setExportMessage(
                            `Preparing Excel rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
                        );
                    },
                }
            );

            setExportProgress(96);
            setExportMessage(
                `Writing Excel workbook (${allRows.length.toLocaleString()} ticket requests)...`
            );
            await yieldToMainThread(40);

            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Claimed Tickets");

            ws["!cols"] = [
                { wch: 6 },  // No.
                { wch: 16 }, // Ticket Code
                { wch: 22 }, // Movie Date & Time
                { wch: 22 }, // Request Date & Time
                { wch: 26 }, // Deputy Name
                { wch: 22 }, // Companion
                { wch: 26 }, // Mall
                { wch: 30 }, // Movie Title
                { wch: 14 }, // Submission
            ];

            XLSX.writeFile(wb, `MTRCB_Claimed_Ticket_Requests_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
            setExportProgress(100);
            setExportMessage("Download complete!");
            await yieldToMainThread(20);
            Notify("success", `Exported all ${allRows.length.toLocaleString()} ticket requests to Excel.`);
        } catch (err: any) {
            console.error("Excel export error:", err);
            Notify("error", err?.message || "Failed to generate Excel export.");
        } finally {
            setIsExporting(false);
            setExportProgress(null);
        }
    };

    const columns: MRT_ColumnDef<ClaimedTicketRequestRow>[] = [
        {
            accessorKey: "number",
            header: "No.",
            size: 65,
            enableSorting: false,
            Cell: ({ row }) => (
                <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                    {(page - 1) * pageSize + row.index + 1}
                </Typography>
            ),
        },
        {
            accessorKey: "movie_datetime",
            header: "Movie Date Time",
            size: 210,
            Cell: ({ cell }) => (
                <Typography sx={{ fontSize: 13, color: "#1E293B" }}>
                    {cell.getValue() as string}
                </Typography>
            ),
        },
        {
            accessorKey: "deputy",
            header: "Deputy",
            size: 230,
            Cell: ({ cell }) => (
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                    {cell.getValue() as string}
                </Typography>
            ),
        },
        {
            accessorKey: "companion",
            header: "Companion",
            size: 200,
            Cell: ({ row }) => {
                const companion = row.original.companion || "No Companion";
                const isNoCompanion = companion === "No Companion";

                return (
                    <Typography
                        sx={{ fontSize: 13 }}
                        color={isNoCompanion ? "text.secondary" : "text.primary"}
                        fontStyle={isNoCompanion ? "italic" : "normal"}
                    >
                        {companion}
                    </Typography>
                );
            },
        },
        {
            accessorKey: "mall",
            header: "Mall",
            size: 210,
            Cell: ({ cell }) => (
                <Typography sx={{ fontSize: 13, color: "#334155" }}>
                    {cell.getValue() as string}
                </Typography>
            ),
        },
        {
            accessorKey: "movie_title",
            header: "Movie Title",
            size: 240,
            Cell: ({ cell }) => (
                <Typography sx={{ fontSize: 13, color: "#334155" }}>
                    {cell.getValue() as string}
                </Typography>
            ),
        },
        {
            accessorKey: "is_offline",
            header: "Submission",
            size: 130,
            enableSorting: false,
            Cell: ({ row }) => {
                const isOffline = row.original.is_offline === "Yes";
                return (
                    <StatusChip
                        label={isOffline ? "Offline" : "Online"}
                        tone={isOffline ? "warning" : "info"}
                    />
                );
            },
        },
        {
            id: "actions",
            header: "Actions",
            size: 130,
            enableSorting: false,
            Cell: ({ row }: any) => (
                <RowActions
                    actions={[
                        {
                            label: "View",
                            onClick: () => setSelectedRow(row.original),
                        },
                    ]}
                />
            ),
        },
    ];

    const sorting = useMemo<MRT_SortingState>(
        () => [{ id: sortBy, desc: sortOrder === "desc" }],
        [sortBy, sortOrder]
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DefaultPage title="Claimed Ticket Requests">
                <Box mb={2.5}>
                    <Alert
                        severity="info"
                        sx={{
                            borderRadius: "6px",
                            fontSize: 13,
                            border: "1px solid #BAE6FD",
                            backgroundColor: "#F0F9FF",
                            "& .MuiAlert-icon": {
                                color: "#0072BC",
                            },
                        }}
                    >
                        Displays claimed monitoring ticket requests from deputies. Filter results
                        and click <strong>Search</strong>. Your filters and pagination persist across views.
                    </Alert>
                </Box>

                {isError && (
                    <Box mb={2.5}>
                        <Alert
                            severity="error"
                            sx={{
                                borderRadius: "6px",
                                fontSize: 13,
                                border: "1px solid #FECACA",
                            }}
                        >
                            Failed to load claimed ticket requests.
                        </Alert>
                    </Box>
                )}

                {/* Filter Container */}
                <Box
                    sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "6px",
                        p: 2,
                        mb: 2,
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
                    }}
                >
                    <Stack spacing={2}>
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={1.5}
                            flexWrap="wrap"
                            useFlexGap
                            alignItems={{ xs: "stretch", md: "center" }}
                        >
                            <DatePicker
                                label="Request Date From"
                                value={draftRequestStart}
                                onChange={(value) => setDraftRequestStart(value)}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: {
                                            ...filterWidth,
                                            "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                                        },
                                    },
                                }}
                            />

                            <DatePicker
                                label="Request Date To"
                                value={draftRequestEnd}
                                onChange={(value) => setDraftRequestEnd(value)}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: {
                                            ...filterWidth,
                                            "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                                        },
                                    },
                                }}
                            />

                            <DatePicker
                                label="Movie Date From"
                                value={draftMovieStart}
                                onChange={(value) => setDraftMovieStart(value)}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: {
                                            ...filterWidth,
                                            "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                                        },
                                    },
                                }}
                            />

                            <DatePicker
                                label="Movie Date To"
                                value={draftMovieEnd}
                                onChange={(value) => setDraftMovieEnd(value)}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: {
                                            ...filterWidth,
                                            "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                                        },
                                    },
                                }}
                            />

                            <TextField
                                size="small"
                                label="Deputy Name"
                                placeholder="Search deputy..."
                                value={draftDeputy}
                                onChange={(e) => setDraftDeputy(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                sx={{
                                    minWidth: 180,
                                    "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                                }}
                            />

                            <TextField
                                size="small"
                                label="Companion"
                                placeholder="Search companion..."
                                value={draftCompanion}
                                onChange={(e) => setDraftCompanion(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                sx={{
                                    minWidth: 180,
                                    "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                                }}
                            />

                            <TextField
                                size="small"
                                label="Movie Title"
                                placeholder="Search movie..."
                                value={draftMovieTitle}
                                onChange={(e) => setDraftMovieTitle(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                sx={{
                                    minWidth: 180,
                                    "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                                }}
                            />

                            <TextField
                                size="small"
                                label="Mall"
                                placeholder="Search mall..."
                                value={draftMall}
                                onChange={(e) => setDraftMall(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                                sx={{
                                    minWidth: 180,
                                    "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
                                }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={draftOfflineOnly}
                                        onChange={(e) => setDraftOfflineOnly(e.target.checked)}
                                        sx={{
                                            color: "#94A3B8",
                                            "&.Mui-checked": { color: "#0072BC" },
                                        }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>
                                        Offline Only
                                    </Typography>
                                }
                            />
                        </Stack>

                        <Divider sx={{ borderColor: "#F1F5F9" }} />

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "center" }}
                            spacing={1.5}
                        >
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<SearchRounded fontSize="small" />}
                                    onClick={handleSearch}
                                    disabled={isFetching}
                                    sx={{
                                        backgroundColor: "#0072BC",
                                        color: "#FFFFFF",
                                        fontWeight: 600,
                                        fontSize: 13,
                                        px: 2,
                                        py: 0.75,
                                        borderRadius: "4px",
                                        textTransform: "none",
                                        boxShadow: "none",
                                        "&:hover": {
                                            backgroundColor: "#005a96",
                                            boxShadow: "none",
                                        },
                                    }}
                                >
                                    {isFetching ? "Searching..." : "Search"}
                                </Button>

                                {hasActiveFilters && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<RestartAltRounded fontSize="small" />}
                                        onClick={handleClear}
                                        disabled={isFetching}
                                        sx={{
                                            color: "#64748B",
                                            borderColor: "#CBD5E1",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: 13,
                                            borderRadius: "4px",
                                            "&:hover": {
                                                borderColor: "#94A3B8",
                                                backgroundColor: "#F8FAFC",
                                            },
                                        }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Stack>

                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<PictureAsPdfRounded fontSize="small" />}
                                    onClick={handleExportPDF}
                                    disabled={isExporting || totalCount === 0}
                                    sx={{
                                        color: "#334155",
                                        borderColor: "#CBD5E1",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: 13,
                                        borderRadius: "4px",
                                        "&:hover": {
                                            borderColor: "#94A3B8",
                                            backgroundColor: "#F8FAFC",
                                        },
                                    }}
                                >
                                    Export PDF
                                </Button>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<TableChartRounded fontSize="small" />}
                                    onClick={handleExportExcel}
                                    disabled={isExporting || totalCount === 0}
                                    sx={{
                                        color: "#0072BC",
                                        borderColor: "#CBD5E1",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: 13,
                                        borderRadius: "4px",
                                        "&:hover": {
                                            borderColor: "#0072BC",
                                            backgroundColor: "#F0F9FF",
                                        },
                                    }}
                                >
                                    Export Excel
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </Box>

                <Box mb={2}>
                    <Alert
                        severity="success"
                        sx={{
                            borderRadius: "6px",
                            fontSize: 13,
                            border: "1px solid #BBF7D0",
                            backgroundColor: "#F0FDF4",
                            py: 0.5,
                            "& .MuiAlert-icon": {
                                color: "#00934C",
                            },
                        }}
                    >
                        Total matched records:{" "}
                        <strong>{Number(totalCount).toLocaleString()}</strong>
                    </Alert>
                </Box>

                <DataTableFive<ClaimedTicketRequestRow>
                    data={data}
                    total={totalCount}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={(p) => {
                        setSelectedRow(null);
                        setPage(Math.max(p, 1));
                    }}
                    columns={columns as any}
                    getRowId={(row) => String(row.id)}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    onRowClick={(row: any) => setSelectedRow(row.original)}
                    sorting={sorting}
                    onSortingChange={(newSorting) => {
                        if (newSorting.length > 0) {
                            updateTableSorting(newSorting[0].id, newSorting[0].desc ? "desc" : "asc");
                        }
                    }}
                    getRowTextColor={(row) =>
                        row.is_offline === "Yes" ? "#D32F2F" : "#0F172A"
                    }
                />

                <TicketRequestDetailsModal
                    open={Boolean(selectedRow)}
                    row={selectedRow}
                    onClose={() => setSelectedRow(null)}
                    onDelete={handleDeleteSelected}
                    isDeleting={isDeleting}
                />

                <ExportLoadingBackdrop
                    open={isExporting}
                    message={exportMessage}
                    progress={exportProgress}
                />
            </DefaultPage>
        </LocalizationProvider>
    );
}